import type { StoreActionApi } from 'react-sweet-state';
import { getFeatureFlagValue } from '@atlassian/jira-feature-flagging';
import { fireOperationalAnalyticsWithContext } from '@atlassian/jira-ui-modifications-analytics';
import type { FieldChangesMapPublic } from '@atlassian/jira-ui-modifications-fields-configuration/src/common/utils/common';
import { ON_INIT_CHANGE_ID } from '../../../../constants';
import type {
	BufferForChangeId,
	AppId,
	ChangeId,
	IssueAdjustmentsState,
	StoreContainerProps,
} from '../../types';
import { setError } from '../errors';
import { validateNoConflictAndApply } from './apply-changes';
import { changesReadyToApply } from './changes-ready-to-apply';
import { clearBufferForChangeId, clearAllBufferTimeouts } from './clear-buffer';
import { handleBufferAllAppsError } from './error-handling';
import { addBufferError, resetBufferErrors } from './errors';
import { sendBufferFinalMetrics, sendBufferConflictMetrics } from './metrics';
import type { BufferContext } from './types';
import { findAppsWhichDidNotSendChangesBeforeTimeout } from './utils';

export {
	clearBufferForChangeId,
	clearAllBufferTimeouts,
	addBufferError,
	resetBufferErrors,
	handleBufferAllAppsError,
	sendBufferFinalMetrics,
	sendBufferConflictMetrics,
};

export const DEFAULT_MULTI_APP_BUFFERING_TIMEOUT_MS = 10000;

const getMultiAppBufferingTimeoutMs = () =>
	getFeatureFlagValue<number>(
		'forge-ui-modifications-multi-app-buffer-timeout',
		DEFAULT_MULTI_APP_BUFFERING_TIMEOUT_MS,
	);

/*
 * How buffering works:
 * -> during fields registration, we check if we need to buffer any apps (if multiple apps registered the same field
 *    for the same changeId). ChangeId is the same for multiple apps and reflects one event in Jira UI (e.g. the same onInit
 *    or the same onChange).
 * -> in useSubmit, when app sends data to Jira, we check if this app should be buffered for this changeId. If so,
 *    we buffer the changes instead af applying them to the UI.
 * -> when we have the first match of two apps registered the same field, we start timeout for the specific changeId.
 * -> we wait until all the apps a) send changes (call submit expected amount of times) b) timeout
 * -> we check if there's a conflict:
 *    if conflict exists: we dismiss all the buffered apps changes, display error to the user and send error to the app.
 *    if there's no conflict: we apply changes sequentially.
 */
export const bufferChanges =
	({
		changeId,
		appId,
		fieldsChanges,
		context,
	}: {
		changeId: ChangeId;
		appId: AppId;
		fieldsChanges: FieldChangesMapPublic;
		context: BufferContext;
	}) =>
	async ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => {
		const { buffer, bufferTimeouts } = getState();
		const previousBuffer = buffer[changeId] || {};
		const previousAppBuffer = previousBuffer[appId] || {};

		const bufferForChangeId = {
			...previousBuffer,
			[appId]: {
				...previousAppBuffer,
				...fieldsChanges,
			},
		};

		if (changesReadyToApply(bufferForChangeId)) {
			clearTimeout(bufferTimeouts[changeId]);
			try {
				await dispatch(validateNoConflictAndApply(bufferForChangeId, changeId, context));
			} catch (err) {
				dispatch(handleBufferAllAppsError({ err, changeId, bufferForChangeId }));
			}
		} else {
			setState({
				buffer: {
					...buffer,
					[changeId]: bufferForChangeId,
				},
			});
		}

		if (bufferTimeouts[changeId] === undefined) {
			const timeout = setTimeout(async () => {
				try {
					await dispatch(bufferTimeout(changeId, context));
				} catch (err) {
					dispatch(handleBufferAllAppsError({ err, changeId, bufferForChangeId }));
				}
			}, getMultiAppBufferingTimeoutMs());

			setState({
				bufferTimeouts: {
					...bufferTimeouts,
					[changeId]: timeout,
				},
			});
		}
	};

export const bufferTimeout =
	(changeId: ChangeId, context: BufferContext) =>
	async (
		{ getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
		{ viewType, createAnalyticsEvent }: StoreContainerProps,
	) => {
		const { buffer, bufferErrors } = getState();
		const bufferForChangeId = buffer[changeId] || {};
		const appsWithTimeout: AppId[] = findAppsWhichDidNotSendChangesBeforeTimeout(bufferForChangeId);

		if (appsWithTimeout.length) {
			// display timeout flag only if other buffering errors did not appear previously to the user
			if (bufferErrors === null) {
				dispatch(setError({ errorCode: 'BUFFERING_TIMEOUT' }));
			}

			dispatch(addBufferError('BUFFERING_TIMEOUT'));

			fireOperationalAnalyticsWithContext(createAnalyticsEvent({}), 'buffer timeout', viewType, {
				changeId,
				allAppIds: Object.keys(bufferForChangeId).join(),
				timedOutAppIds: appsWithTimeout.join(),
			});
		}

		await dispatch(validateNoConflictAndApply(bufferForChangeId, changeId, context));
	};

/*
 * Here we need to initialize the buffer for onInitChangeId for every app as soon as we know that there
 * are multiple apps configured for the context.
 * This is a fix for "Quick app" scenario, which happens quite often:
 * when App 1 sends changes earlier than App 2 registers fields.
 */
export const initializeOnInitBuffer =
	(appsIds: AppId[]) =>
	({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => {
		const { buffer } = getState();
		const bufferForOnInit: BufferForChangeId = {};

		appsIds.forEach((appId) => {
			bufferForOnInit[appId] = null;
		});

		setState({
			buffer: {
				...buffer,
				[ON_INIT_CHANGE_ID]: bufferForOnInit,
			},
		});
	};
